import { X } from "lucide-react";

/**
 * ScreenerPreview — read-only preview of a screener template, rendered like the consumer-facing form.
 * Used by Admin (review before publishing) and Clinic Portal (review before deploying).
 */
export default function ScreenerPreview({ screener, clinicName, onClose }) {
  if (!screener) return null;
  const label = clinicName || "Your clinic";

  return (
    <div
      data-testid="screener-preview-modal"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-2xl h-full bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600">
              Preview · {label}
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">{screener.name}</h2>
          </div>
          <button
            type="button"
            data-testid="screener-preview-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 rounded-full w-8 h-8 border border-slate-200 flex items-center justify-center"
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {screener.intro && <p className="text-slate-700 leading-relaxed">{screener.intro}</p>}

          <div className="space-y-4">
            {(screener.questions || []).map((q, idx) => (
              <div
                key={q.id || idx}
                data-testid={`screener-preview-q-${idx}`}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1">
                  Question {idx + 1} · {q.type}
                </div>
                <div className="font-display text-base font-semibold text-slate-900 mb-3">{q.prompt}</div>
                {q.type === "text" && (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
                    Free-text response field
                  </div>
                )}
                {q.type === "single" && (
                  <ul className="space-y-2">
                    {(q.options || []).map((opt) => (
                      <li
                        key={opt}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 border border-slate-200 bg-slate-50/40"
                      >
                        <span className="w-3.5 h-3.5 rounded-full border border-slate-300" />
                        <span className="text-sm text-slate-800">{opt}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {q.type === "multi" && (
                  <ul className="space-y-2">
                    {(q.options || []).map((opt) => (
                      <li
                        key={opt}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 border border-slate-200 bg-slate-50/40"
                      >
                        <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" />
                        <span className="text-sm text-slate-800">{opt}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {(!screener.questions || screener.questions.length === 0) && (
              <div className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                No questions on this template yet.
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
            Consumers see this exact flow at <code className="text-slate-700">/s/&lt;deployment-slug&gt;</code> after the
            clinic deploys it. Submissions auto-route into the clinic's CRM.
          </div>
        </div>
      </aside>
    </div>
  );
}
